import { Environment, Network, Observable, RecordSource, Store, type GraphQLResponse } from 'relay-runtime';
import { RequestParameters } from 'relay-runtime/lib/util/RelayConcreteNode';
import type { CacheConfig, Variables } from 'relay-runtime/lib/util/RelayRuntimeTypes';
import * as Sentry from '@sentry/react';
import type { Scope } from '@sentry/react';
import { v4 as uuid } from 'uuid';
import { store } from './redux/store';
import { resetUser } from './redux/reducers/userSlice';
import { isTokenExpired } from './utils/isTokenExpired';
import { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import refreshTokenOrLogout from './utils/refreshTokenOrLogout';
import { getSentryUserContext } from './utils/sentryUserContext';
import { PostHog } from 'posthog-js';
import { GraphqlRequestError } from './utils/graphqlRequestError';
import fetchWithRetry from './bffApiCalls/fetchWithRetry';
import { LEARNOSITY_STARTUP_RETRY_STATUSES } from './bffApiCalls/fetchLearnosityStartupData';
import { throwIfAborted } from './utils/fetchCancellation';

function truncateString(value: unknown, maxLen = 500): string {
  const s = String(value ?? '');
  return s.length > maxLen ? `${s.slice(0, maxLen - 3)}…` : s;
}

type GraphQLErrorShape = { message: string; extensions?: { code?: string } };

type GraphqlNetworkFailure = {
  error: Error;
  eventId: string;
};

function normalizeGraphqlNetworkError(cause: unknown): Error {
  const normalizedError = new Error('GraphQL network request failed');
  normalizedError.name = 'GraphQLNetworkError';

  if (cause instanceof Error) {
    Object.defineProperty(normalizedError, 'cause', {
      configurable: true,
      value: cause,
      writable: true,
    });
  }

  return normalizedError;
}

function getGraphqlErrorSignatures(errors: GraphQLErrorShape[]): string[] {
  const signatures = errors.map(error => {
    const code = error.extensions?.code;
    return code ? `code:${code}` : `message:${truncateString(error.message, 300)}`;
  });

  return [...new Set(signatures)].sort();
}

function configureGraphqlScope(scope: Scope, params: RequestParameters, correlationId: string): void {
  const user = getSentryUserContext();

  scope.setUser(user.user?.id ? { id: user.user.id } : null);
  scope.setTag('user_role', user.user_role ?? null);
  scope.setTag('network_id', user.network_id ?? null);
  scope.setTag('grade_level_id', user.grade_level_id ?? null);
  if (user.site_0) scope.setTag('site_0', user.site_0);
  if (user.site_1) scope.setTag('site_1', user.site_1);
  if (user.site_2) scope.setTag('site_2', user.site_2);
  scope.setTag('source', 'graphql');
  scope.setTag('graphql_operation', params.name ?? 'unknown');
  scope.setTag('graphql_kind', params.operationKind ?? 'unknown');
  scope.setTag('correlation_id', correlationId);
}

function reportGraphqlToSentry(args: {
  level: 'error' | 'warning';
  url: string;
  params: RequestParameters;
  correlationId: string;
  http?: { status: string; statusText: string };
  gqlErrors?: GraphQLErrorShape[];
}) {
  const { level, url, params, correlationId, http, gqlErrors } = args;
  const messages = gqlErrors?.map(e => truncateString(e.message, 300)).join(' | ') ?? null;

  Sentry.withScope(scope => {
    scope.setLevel(level);
    configureGraphqlScope(scope, params, correlationId);

    scope.setContext('graphql', {
      operationName: params.name ?? null,
      operationKind: params.operationKind ?? null,
      operationId: params.id ?? null,
      errorCount: gqlErrors?.length ?? null,
      errorMessages: messages,
    });

    // GraphQL errors (when present)
    if (gqlErrors && gqlErrors.length > 0) {
      scope.setExtra('graphql_error_messages', messages);

      const codes = gqlErrors.map(e => e.extensions?.code).join();

      if (codes.length > 0) {
        scope.setTag('graphql_error_codes', codes);
      }

      const responseError = new Error('GraphQL response returned errors');
      responseError.name = 'GraphQLResponseError';
      scope.setFingerprint(['graphql', 'response', params.name ?? 'unknown', ...getGraphqlErrorSignatures(gqlErrors)]);
      Sentry.captureException(responseError);
      return;
    }

    // HTTP error without GraphQL errors payload
    if (http) {
      const statusText = truncateString(http.statusText, 200);
      scope.setTag('http_status', http.status);
      scope.setContext('http', {
        url,
        status: http.status,
        statusText,
      });

      const httpError = new Error('GraphQL HTTP request failed');
      httpError.name = 'GraphQLHttpError';
      scope.setFingerprint(['graphql', 'http', params.name ?? 'unknown', http.status]);
      Sentry.captureException(httpError);
    }
  });
}

function captureGraphqlNetworkFailure(
  error: unknown,
  params: RequestParameters,
  correlationId: string,
): GraphqlNetworkFailure {
  const normalizedError = normalizeGraphqlNetworkError(error);
  const eventId = Sentry.withScope(scope => {
    scope.setLevel('error');
    configureGraphqlScope(scope, params, correlationId);
    scope.setTag('graphql_network_error', 'true');
    scope.setFingerprint(['graphql', 'network', params.name ?? 'unknown']);
    scope.setContext('graphql', {
      operationName: params.name ?? 'unknown',
      operationKind: params.operationKind ?? 'unknown',
      operationId: params.id ?? null,
      networkError: true,
    });

    return Sentry.captureException(normalizedError);
  });

  return { error: normalizedError, eventId };
}

async function fetchGraphQL(
  params: RequestParameters,
  variables: Variables,
  cacheConfig: CacheConfig,
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
  signal?: AbortSignal,
) {
  throwIfAborted(signal);
  // Items API recovery captures one terminal failure. Capturing its Error here
  // first would make Sentry discard that later report as an already-seen object.
  const captureFailure = !(
    params.name === 'IndividualStudentReportInitObjectQuery' && cacheConfig.metadata?.learnosityRecovery === true
  );
  let accessToken = store.getState().accessToken.value;

  if (isTokenExpired()) {
    accessToken = (await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog, signal)) || null;
  }
  throwIfAborted(signal);
  const url = import.meta.env.VITE_GRAPHQL_SERVICE_URL;
  const body = JSON.stringify({ query: params.text, variables });
  // Keep request correlation independent of Sentry traces, which can span multiple requests.
  const correlationId = uuid();

  const init: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Sentry-Trace-Id': correlationId,
    },
    body,
    ...(signal && { signal }),
  };
  let response: Response;
  try {
    // Report signing retries startup reads; its Observable teardown cancels fetch and backoff together.
    response =
      params.name === 'IndividualStudentReportInitObjectQuery'
        ? await fetchWithRetry(url, init, undefined, undefined, { httpStatuses: LEARNOSITY_STARTUP_RETRY_STATUSES })
        : await fetch(url, init);
  } catch (error) {
    throwIfAborted(signal);
    if (!captureFailure)
      throw new GraphqlRequestError(
        { category: 'network', operationName: params.name ?? 'unknown', correlationId },
        error instanceof Error ? error : undefined,
      );
    throw captureGraphqlNetworkFailure(error, params, correlationId).error;
  }

  throwIfAborted(signal);
  if (response.status === 401 || response.status === 403) {
    store.dispatch(resetUser());
    logout({ logoutParams: { returnTo: window.location.origin } });

    return {
      data: null,
      errors: [{ message: 'Unauthorized' }],
    };
  }

  if (!response.ok) {
    if (!captureFailure)
      throw new GraphqlRequestError({
        category: 'http',
        operationName: params.name ?? 'unknown',
        correlationId,
        status: response.status,
        statusText: truncateString(response.statusText, 200),
      });
    reportGraphqlToSentry({
      level: 'error',
      url,
      params,
      correlationId,
      http: { status: String(response.status), statusText: response.statusText },
    });

    return {
      data: null,
      errors: [{ message: truncateString(response.statusText, 200) }],
    };
  }

  const responseFailure = (errors: unknown[]) =>
    new GraphqlRequestError({
      category: 'graphql',
      operationName: params.name ?? 'unknown',
      correlationId,
      status: response.status,
      errorCodes: [
        ...new Set(
          errors.flatMap(error => {
            const code = (error as GraphQLErrorShape | null)?.extensions?.code;
            if (typeof code !== 'string') return [];
            const normalized = code.trim().toUpperCase();
            return /^[A-Z][A-Z0-9_]{0,63}$/.test(normalized) ? [normalized] : [];
          }),
        ),
      ]
        .sort()
        .slice(0, 10),
      errorMessages: errors.slice(0, 10).map(error => {
        const message = (error as GraphQLErrorShape | null)?.message;
        return typeof message === 'string' ? truncateString(message, 300) : 'GraphQL response returned errors';
      }),
    });

  const invalidPayloadFailure = () =>
    new GraphqlRequestError({
      category: 'invalid_payload',
      operationName: params.name ?? 'unknown',
      correlationId,
      status: response.status,
    });
  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    throwIfAborted(signal);
    if (!captureFailure) throw invalidPayloadFailure();
    throw error;
  }
  throwIfAborted(signal);

  if (!captureFailure && Array.isArray(payload?.errors) && payload.errors.length > 0) {
    throw responseFailure(payload.errors);
  }

  if (captureFailure && payload?.errors && Array.isArray(payload.errors) && payload.errors.length > 0) {
    reportGraphqlToSentry({
      level: 'error',
      url,
      params,
      correlationId,
      gqlErrors: payload.errors,
    });
  }

  if (!payload) {
    if (!captureFailure) throw invalidPayloadFailure();
    return {
      data: null,
      errors: [{ message: 'Invalid JSON response from GraphQL service.' }],
    };
  }

  return payload;
}

export const relayEnvironment = (
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
) =>
  new Environment({
    network: Network.create((params: RequestParameters, variables: Variables, cacheConfig: CacheConfig) => {
      if (params.name !== 'IndividualStudentReportInitObjectQuery')
        return fetchGraphQL(params, variables, cacheConfig, getAccessTokenSilently, logout, posthog);
      return Observable.create<GraphQLResponse>(sink => {
        const controller = new AbortController();
        void fetchGraphQL(
          params,
          variables,
          cacheConfig,
          getAccessTokenSilently,
          logout,
          posthog,
          controller.signal,
        ).then(
          payload => {
            if (controller.signal.aborted) return;
            sink.next(payload);
            sink.complete();
          },
          error => {
            if (!controller.signal.aborted) sink.error(error);
          },
        );
        return () => controller.abort();
      });
    }),
    store: new Store(new RecordSource()),
  });
